/**
 * Authentication Wrapper
 * Wraps the main app and handles authentication state
 */

import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import LoginPage from './LoginPage';
import AdminPanel from './AdminPanel';

const STORAGE_KEY = 'cipheriqSession';

const AuthWrapper = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userAttributes, setUserAttributes] = useState(null);
  const [sessionTokens, setSessionTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    const storedSession = localStorage.getItem(STORAGE_KEY);
    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);
        setSessionTokens(parsed);
        setIsAuthenticated(true);
      } catch (err) {
        console.warn('Failed to parse stored session, ignoring');
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (authData) => {
    console.log('handleLogin success - storing session locally');
    const sessionPayload = authData || { timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionPayload));
    setSessionTokens(sessionPayload);
    setIsAuthenticated(true);
    setLoading(false);
    // Attempt to fetch attributes asynchronously, but do not block UI
    authService
      .getUserAttributes()
      .then((attributes) => {
        setUserAttributes(attributes);
        return authService.isAdmin();
      })
      .then((adminStatus) => setIsAdmin(adminStatus))
      .catch((error) => {
        console.log('Attribute/admin fetch deferred:', error.message);
      });
  };

  const handleLogout = () => {
    authService.signOut();
    localStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUserAttributes(null);
    setSessionTokens(null);
    setShowAdminPanel(false);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2>Loading...</h2>
          <p>Verifying authentication</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLogin} />;
  }

  if (showAdminPanel && isAdmin) {
    return (
      <AdminPanel 
        onLogout={handleLogout}
        onClose={() => setShowAdminPanel(false)}
      />
    );
  }

  return (
    <div>
      {/* User info bar */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <span style={{ fontWeight: '500' }}>
            👤 {userAttributes?.name || userAttributes?.email}
          </span>
          {isAdmin && (
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              👑 Admin
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isAdmin && (
            <button
              onClick={() => setShowAdminPanel(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              ⚙️ Admin Panel
            </button>
          )}
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(220, 53, 69, 0.9)',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#c82333'}
            onMouseOut={(e) => e.target.style.background = 'rgba(220, 53, 69, 0.9)'}
          >
            🚪 Sign Out
          </button>
        </div>
      </div>

      {/* Main app content */}
      {children}
    </div>
  );
};

export default AuthWrapper;
